import React, { useMemo, useState } from 'react';
import { PlusIcon, RotateCcwIcon, SearchIcon, Trash2Icon, TrophyIcon, XIcon } from 'lucide-react';
import type { Entry } from '../types/raffle';
interface EntryPanelProps {
  entries: Entry[];
  activeCount: number;
  drawnCount: number;
  history: Entry[];
  disabled: boolean;
  removeWinners: boolean;
  onAdd: (names: string[]) => void;
  onRemove: (id: string) => void;
  onResetWinners: () => void;
  onClearAll: () => void;
}
const HISTORY_PREVIEW = 10;
export function EntryPanel({
  entries,
  activeCount,
  drawnCount,
  history,
  disabled,
  removeWinners,
  onAdd,
  onRemove,
  onResetWinners,
  onClearAll
}: EntryPanelProps) {
  const [value, setValue] = useState('');
  const [query, setQuery] = useState('');
  const submit = () => {
    const names = value.split(/[,\n\t]/).map(n => n.trim()).filter(Boolean);
    if (names.length === 0) return;
    onAdd(names);
    setValue('');
  };
  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return q ? entries.filter(e => e.name.toLowerCase().includes(q)) : entries;
  }, [entries, query]);
  return <section aria-labelledby="roster-heading" className="flex min-h-0 flex-1 flex-col" data-id="element-84" data-mp="3kcgwrabt6ljx">
      <div className="flex items-baseline justify-between gap-3" data-id="element-85" data-mp="3oc8wcxj85hd5">
        <h2 id="roster-heading" className="text-lg font-semibold text-white" data-id="element-86" data-mp="3td5as517clwt">
          Names
        </h2>
        <p className="text-xs tabular-nums text-muted" data-id="element-87" data-mp="27dj317s117w">
          {activeCount} in the draw
          {removeWinners && drawnCount > 0 ? ` · ${drawnCount} drawn` : ''}
        </p>
      </div>

      <form onSubmit={e => {
      e.preventDefault();
      submit();
    }} className="mt-4" data-id="element-88" data-mp="bw58p2pd79fn">
        <label className="sr-only" htmlFor="name-input" data-id="element-89" data-mp="fxh3rnr0cmv8">
          Add names
        </label>
        <textarea id="name-input" rows={2} value={value} onChange={e => setValue(e.target.value)} onKeyDown={e => {
        if (e.key === 'Enter' && !e.shiftKey) {
          e.preventDefault();
          submit();
        }
      }} disabled={disabled} placeholder="Add a name, or paste a whole list" className="w-full resize-y rounded-lg border border-line bg-raised px-3 py-2.5 text-sm text-white placeholder:text-muted/70 focus:border-gold focus:outline-none disabled:opacity-50" data-id="element-90" data-mp="kyely0c0pgwq" />
        <div className="mt-2 flex items-center justify-between gap-3" data-id="element-91" data-mp="34ohlnqd3fytl">
          <p className="text-[11px] leading-tight text-muted" data-id="element-92" data-mp="30oqrrds9t2ku">
            One per line or comma-separated. Duplicates are skipped.
          </p>
          <button type="submit" disabled={disabled || value.trim().length === 0} className="inline-flex shrink-0 items-center gap-1.5 rounded-lg bg-gold px-3.5 py-2 text-sm font-semibold text-ink transition-[background-color,transform] duration-150 ease-emphasis hover:bg-[#F0C154] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold active:scale-[0.97] disabled:cursor-not-allowed disabled:bg-line disabled:text-muted disabled:active:scale-100" data-id="element-93" data-mp="3en75hoojvtch">
            <PlusIcon className="h-4 w-4" aria-hidden="true" data-id="element-94" data-mp="3h8y4dn3e54rf" />
            Add
          </button>
        </div>
      </form>

      {entries.length > 20 && <div className="relative mt-4" data-id="element-95" data-mp="3m5erqf2rnf3g">
          <SearchIcon className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted" aria-hidden="true" data-id="element-96" data-mp="38771eq08jc19" />
          <label className="sr-only" htmlFor="name-search" data-id="element-97" data-mp="3d4gohz05jblx">
            Search names
          </label>
          <input id="name-search" value={query} onChange={e => setQuery(e.target.value)} placeholder={`Search ${entries.length} names`} className="w-full rounded-lg border border-line bg-raised py-2 pl-9 pr-3 text-sm text-white placeholder:text-muted/70 focus:border-gold focus:outline-none" data-id="element-98" data-mp="338nccgqchk4o" />
        </div>}

      <div className="mt-4 min-h-0 flex-1 overflow-y-auto pr-1" data-id="element-99" data-mp="2xiauko9oerhu">
        {entries.length === 0 ? <p className="py-8 text-center text-sm text-muted" data-id="element-100" data-mp="2tgz5qkymmaap">
            No names yet. Add at least two to spin.
          </p> : visible.length === 0 ? <p className="py-8 text-center text-sm text-muted" data-id="element-101" data-mp="2jjuj6k0vcctd">No names match “{query}”.</p> : <ul className="divide-y divide-line/70" data-id="element-102" data-mp="2en41js9f195u">
            {visible.map(entry => <li key={entry.id} className="group flex items-center justify-between gap-3 py-2" data-id="element-103" data-mp="29u177hwtdvol">
                <span className={`truncate text-sm ${entry.drawn && removeWinners ? 'text-muted line-through decoration-muted/60' : 'text-body'}`} data-id="element-104" data-mp="20u4nlupa7n0e">
                  {entry.name}
                </span>
                <div className="flex shrink-0 items-center gap-2" data-id="element-105" data-mp="1gsjksza4u6eq">
                  {entry.drawn && <TrophyIcon className="h-3.5 w-3.5 text-gold" aria-label="Already won" data-id="element-106" data-mp="1lqbmum4hr8wn" />}
                  <button type="button" onClick={() => onRemove(entry.id)} disabled={disabled} aria-label={`Remove ${entry.name}`} className="rounded p-1 text-muted opacity-0 transition-colors duration-150 ease-emphasis hover:text-crimson focus-visible:opacity-100 focus-visible:outline focus-visible:outline-1 focus-visible:outline-gold group-hover:opacity-100 disabled:opacity-0" data-id="element-107" data-mp="1qr94mxlla0xm">
                    <XIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-108" data-mp="1crhr0xyzimk5" />
                  </button>
                </div>
              </li>)}
          </ul>}
      </div>

      {history.length > 0 && <div className="mt-5 border-t border-line pt-4" data-id="element-109" data-mp="k8kj23u8pghl">
          <h3 className="text-xs font-semibold uppercase tracking-[0.16em] text-muted" data-id="element-110" data-mp="c8xqk4f2iv2o">
            Drawn so far
          </h3>
          <ol className="mt-3 space-y-1.5" data-id="element-111" data-mp="79l8dwz56c6f">
            {history.slice(0, HISTORY_PREVIEW).map((entry, i) => <li key={`${entry.id}-${i}`} className="flex items-baseline gap-2 text-sm" data-id="element-112" data-mp="lyhdbl5wj92t" data-mpi={i}>
                <span className="w-6 shrink-0 tabular-nums text-xs text-muted" data-id="element-113" data-mp="h0o9gys2mr5p">
                  {history.length - i}
                </span>
                <span className="truncate text-body" data-id="element-114" data-mp="3kgwfq71s87uq">{entry.name}</span>
              </li>)}
          </ol>
          {history.length > HISTORY_PREVIEW && <p className="mt-2 pl-8 text-xs text-muted" data-id="element-115" data-mp="3a2nv2b8g344">
              + {history.length - HISTORY_PREVIEW} earlier
            </p>}
        </div>}

      <div className="mt-5 border-t border-line pt-4" data-id="element-116" data-mp="1emvbq20wk7ew">
        <div className="flex flex-wrap gap-4 text-xs" data-id="element-117" data-mp="1epr7bjpa2k06">
          <button type="button" onClick={onResetWinners} disabled={disabled || drawnCount === 0 && history.length === 0} className="inline-flex items-center gap-1.5 font-semibold text-body transition-colors duration-150 ease-emphasis hover:text-gold focus-visible:outline focus-visible:outline-1 focus-visible:outline-gold disabled:cursor-not-allowed disabled:text-muted/50" data-id="element-118" data-mp="19o1y89lm8geq">
            <RotateCcwIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-119" data-mp="1cb2vkyb8120u" />
            Clear winners
          </button>
          <button type="button" onClick={onClearAll} disabled={disabled || entries.length === 0} className="inline-flex items-center gap-1.5 font-semibold text-body transition-colors duration-150 ease-emphasis hover:text-crimson focus-visible:outline focus-visible:outline-1 focus-visible:outline-gold disabled:cursor-not-allowed disabled:text-muted/50" data-id="element-120" data-mp="24xno423fnk5z">
            <Trash2Icon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-121" data-mp="2noydufxf9qwa" />
            Clear all
          </button>
        </div>
        <p className="mt-3 text-[11px] text-muted" data-id="element-122" data-mp="1yyk9um99rbd0">
          Saved on this device — names and winners survive a refresh.
        </p>
      </div>
    </section>;
}